import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  type ElementRef,
  input,
  output,
  viewChildren,
} from '@angular/core';
import type { Icon } from '@frontendlib/common/providers/icons/icons';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import {
  findHubSortOption,
  HUB_FILTER_OPTIONS,
  HUB_SORT_OPTIONS,
  type HubFilterOption,
  type HubFilters,
  type HubSortField,
  type HubSortOption,
} from '../hub-sort';

let nextSortHeadingId = 0;
let nextFilterHeadingId = 0;

interface HubSortOptionView extends HubSortOption {
  readonly isActive: boolean;
}

interface HubFilterOptionView extends HubFilterOption {
  readonly checked: boolean;
  readonly icon: Icon;
}

/**
 * The sort menu's contents, split from the trigger so they can be tested: a popover renders through
 * a CDK overlay, which produces nothing under jsdom, and a menu that no test can open is a menu no
 * test can check.
 */
@Component({
  selector: 'app-entity-hub-sort-menu',
  standalone: true,
  templateUrl: './entity-hub-sort-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: {
    class: 'block',
  },
})
export class EntityHubSortMenuComponent {
  public readonly value = input.required<HubSortField>();
  public readonly filters = input.required<HubFilters>();

  public readonly sortChange = output<HubSortField>();
  public readonly filtersChange = output<HubFilters>();
  public readonly tabOut = output<void>();

  private readonly menuItems = viewChildren<ElementRef<HTMLButtonElement>>('menuItem');

  protected readonly sortHeadingId = `hub-sort-heading-${nextSortHeadingId++}`;
  protected readonly filterHeadingId = `hub-filter-heading-${nextFilterHeadingId++}`;

  protected readonly items = computed<HubSortOptionView[]>(() => {
    const active = findHubSortOption(this.value());

    return HUB_SORT_OPTIONS.map((option) => ({ ...option, isActive: option === active }));
  });

  protected readonly filterItems = computed<HubFilterOptionView[]>(() => {
    const filters = this.filters();

    return HUB_FILTER_OPTIONS.map((option) => ({
      ...option,
      checked: filters[option.key],
      icon: filters[option.key] ? 'sapphire-checkbox-on' : 'sapphire-checkbox-off',
    }));
  });

  constructor() {
    afterNextRender(() => this.menuItems()[0]?.nativeElement.focus());
  }

  protected onMenuKeydown(event: KeyboardEvent): void {
    const items = this.menuItems().map((item) => item.nativeElement);
    const at = items.findIndex((item) => item === event.target);

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items[(at + step + items.length) % items.length]?.focus();
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      items[event.key === 'Home' ? 0 : items.length - 1]?.focus();
    } else if (event.key === 'Tab') {
      this.tabOut.emit();
    }
  }

  protected handleSelect(option: HubSortOptionView): void {
    if (!option.isActive) {
      this.sortChange.emit(option.value);
    }
  }

  protected toggleFilter(option: HubFilterOptionView): void {
    this.filtersChange.emit({ ...this.filters(), [option.key]: !option.checked });
  }
}
